import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import { memo, useCallback, useMemo, useState } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import Foreign from './foreign';
import Italian from './italian';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { FormikErrors, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { CohabitationDetailForm } from 'cohabitation/view-models/detail-cohabitation';
import { AnprVM } from 'shell/view-models/search-anpr';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { PersonVM } from 'shell/view-models/search-person';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { HeadCell } from 'shell/components/shared/table/types';

const headCells: HeadCell<PersonVM, keyof PersonVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'action',
    label: 'Azione',
  },
];

interface FormGeneralityProps {
  isEditMode?: boolean;
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  person?: AccordionPersonForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  errorsPerson?: FormikErrors<AccordionPersonForm>;
  touchedPerson?: FormikTouched<AccordionPersonForm>;
  onChangePerson: (
    key: keyof AccordionPersonForm,
    value: AccordionPersonForm[keyof AccordionPersonForm]
  ) => void;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeGeneralityValues: (values: AccordionGeneralityForm) => void;
  onChangeValues: (values: CohabitationDetailForm) => void;
  resetForm: () => void;
}

const FormGeneralityComponent = ({
  isEditMode,
  disabled,
  person,
  generality,
  errorsPerson,
  touchedPerson,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  onChangeGeneralityValues,
  onChangePerson,
  onChangeValues,
  resetForm,
}: FormGeneralityProps): React.ReactElement => {
  const [tableVisibility, setTableVisibility] = useState<boolean>(false);

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const [anpr, getAnpr] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    AnprVM<{
      person?: AccordionPersonForm;
      generality?: AccordionGeneralityForm;
    }>
  >(`${__PERSON_NEW_API_URL__}/persona`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => {
      const subjects = response.data.payload?.risposta3002OK?.listaSoggetti.datiSoggetto || [];
      const persons = subjects.map((subject) => {
        const generalityAnpr = transformGenerality(subject || {}) || undefined;

        return {
          person: {
            fiscalCode: subject?.generalita?.codiceFiscale?.codFiscale,
            name: subject?.generalita?.nome,
            surname: subject?.generalita?.cognome,
            withoutName: subject?.generalita?.senzaNome === '1' ? true : false,
            withoutSurname: subject?.generalita?.senzaCognome === '1' ? true : false,
          },
          generality: generalityAnpr,
        };
      });

      return {
        persons,
        warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
          (warning) => ({
            id: warning.codiceErroreAnomalia,
            value: warning.testoErroreAnomalia,
          })
        ),
        errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
          id: error.codiceErroreAnomalia,
          value: error.testoErroreAnomalia,
        })),
      };
    },
  });

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreign', {
        ...(generality || {}).foreign,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const handleListGenderChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('gender', event.target.value);
  });

  const handleChooseDateChange = useEventCallback(
    (eventDate: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeGeneralityValues({ birthChoiceDate: choice, birthDate: eventDate });
    }
  );

  const handleSearch = useEventCallback(() => {
    (
      getAnpr({
        data: {
          criteriRicerca: {
            codiceFiscale: person?.fiscalCode || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: '0',
          },
        },
      }) as ResponsePromise<
        HttpResponse<ResponseAnprData>,
        AnprVM<{
          person?: AccordionPersonForm;
          generality?: AccordionGeneralityForm;
        }>
      >
    ).then((response) => {
      const responseCasted = response as AnprVM<{
        person?: AccordionPersonForm;
        generality?: AccordionGeneralityForm;
      }>;

      if (responseCasted?.persons?.length === 1) {
        const [element] = responseCasted?.persons || [];

        onChangeValues(element);
      }

      if (responseCasted?.persons?.length > 1) {
        setTableVisibility(true);
      }

      setVisibility({ error: undefined, warning: undefined });
      resetPagination();
    });
  });

  const castedAnprData = anpr.data as AnprVM<{
    person?: AccordionPersonForm;
    generality?: AccordionGeneralityForm;
  }>;

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, anpr.data]);

  const handleClickSelect = useEventCallback(
    (subject: { person?: AccordionPersonForm; generality?: AccordionGeneralityForm }) => {
      onChangeValues(subject);
      setTableVisibility(false);
    }
  );

  const dataPersons: Array<PersonVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((subject) => ({
        fiscalCode: subject.person?.fiscalCode || '',
        surname: subject.person?.surname || '',
        name: subject.person?.name || '',
        gender: subject.generality?.gender || '',
        birthday: subject.generality?.birthDate?.toFormat('dd/MM/yyyy') || '',
        action: (
          <PmiButton
            onClick={() => handleClickSelect(subject)}
            variant="outlined"
            color="cdmBrandThird"
            id={subject.person?.fiscalCode}
          >
            Seleziona
          </PmiButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons, handleClickSelect]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SearchMessages
          type={MessageType.Error}
          title={errorMessage.title || ''}
          messages={errorMessage.messages || []}
          visibility={errorMessage.visibility || false}
          onChangeVisibility={onVisibility}
        />
        <SearchMessages
          type={MessageType.Warning}
          title={`Ci sono ${castedAnprData?.warnings?.length || ''} warning`}
          messages={castedAnprData?.warnings || []}
          visibility={
            visibility.warning !== undefined
              ? visibility.warning
              : (castedAnprData?.warnings || [])?.length > 0
          }
          onChangeVisibility={onVisibility}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          id="fiscal-code"
          value={person?.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => {
            onChangePerson('fiscalCode', event.target.value);
          }}
          helperText={errorsPerson?.fiscalCode}
          error={touchedPerson?.fiscalCode && Boolean(errorsPerson?.fiscalCode)}
          fullWidth
          disabled={isEditMode ? false : disabled}
          inputProps={{ maxLength: '16' }}
        />
      </Grid>
      <Grid item container spacing={2} xs={12} md={10} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Cognome"
            aria-label="Cognome"
            id="surname"
            fullWidth
            disabled={isEditMode ? false : disabled}
            helperText={errorsPerson?.surname}
            error={touchedPerson?.surname && Boolean(errorsPerson?.surname)}
            value={person?.surname || ''}
            onChange={(event) => {
              onChangePerson('surname', event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname-replica"
                checked={person?.withoutSurname || false}
                onChange={() => onChangePerson('withoutSurname', !person?.withoutSurname)}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2} xs={12} md={10} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Nome"
            aria-label="Nome"
            id="name"
            fullWidth
            disabled={isEditMode ? false : disabled}
            helperText={errorsPerson?.name}
            error={touchedPerson?.name && Boolean(errorsPerson?.name)}
            value={person?.name || ''}
            onChange={(event) => {
              onChangePerson('name', event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName-replica"
                checked={person?.withoutName || false}
                onChange={() => onChangePerson('withoutName', !person?.withoutName)}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={3}>
          <ListGender
            value={generality?.gender || ''}
            onChange={handleListGenderChange}
            disabled={isEditMode ? false : disabled}
            error={Boolean(errorsGenerality?.gender)}
          />
        </Grid>
        <Grid item xs={12} md={2} display="flex" alignSelf="center">
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            id="handle-search-anpr"
            fullWidth
            onClick={handleSearch}
            disabled={disabled}
          >
            Cerca
          </PmiButton>
        </Grid>
      </Grid>
      {dataPersons.length > 1 && tableVisibility ? (
        <Grid item xs={12}>
          <Table
            ariaLabel="Lista Persone"
            page={paginationState.page}
            rowsPerPage={paginationState.rowsPerPage}
            body={dataPersons}
            count={dataPersons.length}
            head={headCells}
            hideButtons
            onSort={noop}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onVisibilityPress={noop}
            orders={{}}
            isNotLayoutFixed
            noSort
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Nascita</PmiTitleDivider>
      </Grid>
      <Grid item xs={12} container spacing={2} alignItems="center">
        <ChooseDate
          selected={generality?.birthDate || null}
          onChange={handleChooseDateChange}
          choice={generality?.birthChoiceDate}
          disabled={isEditMode ? false : disabled}
          error={touchedGenerality?.birthDate && Boolean(errorsGenerality?.birthDate)}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            id="isForeign"
            defaultValue={false}
            value={generality?.isForeign}
            onChange={() => onChangeGenerality('isForeign', !generality?.isForeign)}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Nascita Italia"
              disabled={isEditMode ? false : disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Nascita Estero"
              disabled={isEditMode ? false : disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!generality?.isForeign ? (
        <Italian
          disabled={disabled}
          isEditMode={isEditMode}
          values={generality?.italian || undefined}
          onChange={onFieldItalianChange}
          errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
        />
      ) : (
        <Foreign
          disabled={disabled}
          isEditMode={isEditMode}
          values={generality?.foreign || undefined}
          onChange={onFieldForeignChange}
          errors={errorsGenerality?.foreign as FormikErrors<ForeignForm>}
        />
      )}
      <Grid item xs={12}>
        <PmiTextField
          aria-label="Luogo di nascita eccezionale"
          label="Luogo di nascita eccezionale"
          id="exceptionalBirthplace"
          name="exceptionalBirthplace"
          fullWidth
          disabled={isEditMode ? false : disabled}
          type="text"
          variant="outlined"
          helperText={errorsGenerality?.exceptionalBirthPlace}
          error={
            touchedGenerality?.exceptionalBirthPlace &&
            Boolean(errorsGenerality?.exceptionalBirthPlace)
          }
          value={generality?.exceptionalBirthPlace || ''}
          onChange={(event) => {
            onChangeGenerality('exceptionalBirthPlace', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Identificativo del Comune"
          label="Identificativo del Comune"
          id="municipalityId"
          fullWidth
          disabled={isEditMode ? false : disabled}
          helperText={errorsGenerality?.municipalityIdentifier}
          error={
            touchedGenerality?.municipalityIdentifier &&
            Boolean(errorsGenerality?.municipalityIdentifier)
          }
          type="text"
          variant="outlined"
          value={generality?.municipalityIdentifier || ''}
          onChange={(event) => {
            onChangeGenerality('municipalityIdentifier', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Codice Istat"
          label="Codice Istat"
          id="istatCode"
          fullWidth
          disabled={disabled}
          type="text"
          variant="outlined"
          value={generality?.istatCode || ''}
          onChange={(event) => {
            onChangeGenerality('istatCode', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Identificativo ANPR"
          label="Identificativo ANPR"
          id="anprId"
          name="anprId"
          fullWidth
          disabled={disabled}
          type="text"
          variant="outlined"
          value={generality?.anprIdentifier || ''}
          onChange={(event) => {
            onChangeGenerality('anprIdentifier', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Anno Espatrio"
          id="expatriation-year"
          fullWidth
          disabled={isEditMode ? false : disabled}
          helperText={errorsGenerality?.expatriationYear}
          error={touchedGenerality?.expatriationYear && Boolean(errorsGenerality?.expatriationYear)}
          aria-label="Anno Espatrio"
          value={generality?.expatriationYear || ''}
          onChange={(event) => {
            onChangeGenerality(
              'expatriationYear',
              event.target.validity ? event.target.value : generality?.expatriationYear
            );
          }}
          inputProps={{ maxLength: '4', pattern: '\\d{1,4}' }}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          aria-label="Note"
          label="Note"
          id="note"
          name="note"
          fullWidth
          disabled={isEditMode ? false : disabled}
          helperText={errorsGenerality?.note}
          error={touchedGenerality?.note && Boolean(errorsGenerality?.note)}
          type="text"
          variant="outlined"
          multiline
          rows={10}
          value={generality?.note || ''}
          onChange={(event) => {
            onChangeGenerality('note', event.target.value);
          }}
          inputProps={{
            maxLength: '240',
          }}
        />
      </Grid>
      <Grid item xs={12} md={2} display="flex" alignSelf="center">
        <PmiButton
          variant="outlined"
          color="cdmBrandThird"
          id="handle-reset"
          fullWidth
          onClick={resetForm}
          disabled={disabled}
        >
          Ripulisci
        </PmiButton>
      </Grid>
    </Grid>
  );
};

FormGeneralityComponent.displayName = 'Form generality component';

const FormGenerality = memo(FormGeneralityComponent);

export default FormGenerality;
